// Procedural watercolour wash tiles.
//
// Overlapping pigment blooms with a darker "wet edge" where pigment pools
// at the rim, a granulation pass, then a wrap-aware blur so nothing reads
// as a hard pixel. Each tile is painted once at module load into a small
// canvas and shared by every surface using it, so it costs nothing per
// frame.
//
// Lives in its own module because both the terrain blocks (chunks.ts) and
// the water surface (water.ts) need it, and chunks.ts already imports
// water.ts — putting it in either one would close an import cycle.

import * as THREE from 'three';

// The knobs worth turning when the look is off:
export const S = 128;       // texture resolution
export const WASH_BLUR = 6; // px — how wet/soft the washes read
const BLOOM_COUNT = 7;      // pigment loads per tile
const GRAIN_DENSITY = 0.06; // granulation speckle, as a fraction of pixels

export function makeCanvas(size: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  return c;
}

export function rgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// One pigment load, drawn nine times (itself plus the eight wrapped
// neighbours) so a bloom running off one edge continues on the opposite
// one — these tiles have to stay seamless under RepeatWrapping.
export function bloom(ctx: CanvasRenderingContext2D, color: string, edge: string, cx: number, cy: number, radius: number, alpha: number): void {
  for (let ox = -1; ox <= 1; ox++) {
    for (let oy = -1; oy <= 1; oy++) {
      const x = cx + ox * S, y = cy + oy * S;
      const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
      g.addColorStop(0, rgba(color, alpha * 0.5));
      g.addColorStop(0.7, rgba(color, alpha * 0.8));
      g.addColorStop(0.93, rgba(edge, alpha)); // pigment pools at the rim
      g.addColorStop(1, rgba(edge, 0));
      ctx.fillStyle = g;
      ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }
  }
}

// Blurs without breaking tiling: lays the source out 3x3, blurs that, and
// crops the centre back out, so the blur kernel sees wrapped neighbours
// instead of empty space at the edges. Wall textures pass wrapY = false —
// their v runs 0..1 up the block face and never repeats vertically, so the
// top and bottom edges are clamped instead (otherwise the grass cap would
// bleed a green fringe onto the bottom of every block).
export function blurWrapped(src: HTMLCanvasElement, radius: number, wrapY = true): HTMLCanvasElement {
  const big = makeCanvas(S * 3);
  const bctx = big.getContext('2d')!;
  for (let x = 0; x < 3; x++) {
    if (wrapY) {
      for (let y = 0; y < 3; y++) bctx.drawImage(src, x * S, y * S);
    } else {
      bctx.drawImage(src, 0, 0, S, 1, x * S, 0, S, S);          // top edge, clamped
      bctx.drawImage(src, x * S, S);
      bctx.drawImage(src, 0, S - 1, S, 1, x * S, 2 * S, S, S);  // bottom edge, clamped
    }
  }
  const out = makeCanvas(S);
  const octx = out.getContext('2d')!;
  octx.filter = `blur(${radius}px)`;
  octx.drawImage(big, -S, -S);
  octx.filter = 'none';
  return out;
}

export interface WashOpts {
  blooms?: number;
  // Min/max alpha of a pigment load.
  alpha?: [number, number];
  // Large, soft patches of a second pigment laid under the blooms, at a
  // scale bigger than a single hex.
  //
  // This is what makes a per-hex UV offset worth anything. The default
  // wash swings about 13 levels out of 255 at its strongest and is then
  // blurred, so it is very nearly a flat colour — two hexes cropping
  // different parts of it look identical, however far apart their sample
  // points are. The mottle gives the tile structure to slide through.
  // Passed the same colour as its own wet edge, so these read as soft
  // masses rather than blotches with a pooled rim.
  mottle?: { colors: string[]; count: number; alpha: number };
}

export function washTile(base: string, pigments: string[], edge: string, grain: string,
                         opts: WashOpts = {}): HTMLCanvasElement {
  const { blooms = BLOOM_COUNT, alpha = [0.22, 0.42], mottle } = opts;
  const c = makeCanvas(S);
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);
  if (mottle) {
    for (let i = 0; i < mottle.count; i++) {
      const col = mottle.colors[i % mottle.colors.length];
      bloom(ctx, col, col, Math.random() * S, Math.random() * S,
        S * (0.45 + Math.random() * 0.4), mottle.alpha);
    }
  }
  for (let i = 0; i < blooms; i++) {
    bloom(ctx, pigments[i % pigments.length], edge,
      Math.random() * S, Math.random() * S,
      S * (0.16 + Math.random() * 0.26), alpha[0] + Math.random() * (alpha[1] - alpha[0]));
  }
  ctx.fillStyle = grain;
  for (let i = 0; i < S * S * GRAIN_DENSITY; i++) {
    ctx.globalAlpha = 0.04 + Math.random() * 0.1;
    ctx.fillRect(Math.random() * S, Math.random() * S, 1, 1);
  }
  ctx.globalAlpha = 1;
  return c;
}

export function toTexture(canvas: HTMLCanvasElement, wrapY = true, blur = WASH_BLUR): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(blurWrapped(canvas, blur, wrapY));
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.anisotropy = 4;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}
